var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cx } from '@web/utility';
import { renderTooltipDateHeading } from '@web/utility/ tooltip/tooltipDateLabel';
import { useFilters } from '@web/utility/hooks/useFilters';
import { clsx } from 'clsx';
import React from 'react';
export var TOOLTIP_PANEL_COMPACT_CLASS = 'bg-white p-2 rounded shadow text-sm font-normal text-gray-700';
export var TOOLTIP_PANEL_RECHARTS_CLASS = 'bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[200px]';
export var TOOLTIP_DATE_HEADER_DIVIDER_CLASS = 'border-b border-gray-200 pb-2 mb-2';
export var TOOLTIP_SERIES_SEPARATOR_CLASS = 'border-t border-gray-200 my-1.5';
export function TooltipDateHeaderSection(_a) {
    var children = _a.children, className = _a.className;
    return (_jsx("div", __assign({ className: cx(TOOLTIP_DATE_HEADER_DIVIDER_CLASS, className) }, { children: children })));
}
export function TooltipPanelCompact(_a) {
    var children = _a.children, className = _a.className;
    return (_jsx("div", __assign({ className: clsx(TOOLTIP_PANEL_COMPACT_CLASS, className) }, { children: children })));
}
export function CategoryDotRows(_a) {
    var rows = _a.rows, getDotColorClass = _a.getDotColorClass, separatorAfterKeys = _a.separatorAfterKeys;
    return (_jsx("div", __assign({ className: "flex flex-col gap-0.5" }, { children: rows.flatMap(function (_a, index) {
            var key = _a.key, label = _a.label, value = _a.value;
            var row = (_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [_jsx("span", { className: "inline-block w-2 h-2 rounded-full ".concat(getDotColorClass(key)) }), _jsxs("span", { children: [_jsx("span", __assign({ className: "text-gray-600" }, { children: label })), _jsx("span", __assign({ className: "font-semibold text-gray-900" }, { children: value }))] })] }), "row-".concat(key)));
            var showSep = (separatorAfterKeys === null || separatorAfterKeys === void 0 ? void 0 : separatorAfterKeys.includes(key)) && index < rows.length - 1;
            return showSep
                ? [
                    row,
                    _jsx("div", { className: TOOLTIP_SERIES_SEPARATOR_CLASS, role: "separator" }, "sep-".concat(key))
                ]
                : [row];
        }) })));
}
export var getBarChartTooltip = function (props, categories, _a) {
    var _b, _c;
    var getDotColorClass = _a.getDotColorClass, _d = _a.formatValue, formatValue = _d === void 0 ? function (value) { return String(value); } : _d, referenceDateFrom = _a.referenceDateFrom, referenceDateTo = _a.referenceDateTo, _e = _a.hideWhenAllZero, hideWhenAllZero = _e === void 0 ? false : _e;
    var active = props.active, payload = props.payload;
    if (!active || !(payload === null || payload === void 0 ? void 0 : payload[0]))
        return null;
    var row = payload[0].payload;
    if (!row)
        return null;
    if (!categories.length)
        return null;
    var items = categories.map(function (key) {
        var _a;
        return ({
            key: key,
            value: Number((_a = row[key]) !== null && _a !== void 0 ? _a : 0)
        });
    });
    if (hideWhenAllZero &&
        items.length > 0 &&
        items.every(function (_a) {
            var value = _a.value;
            return value === 0;
        })) {
        return null;
    }
    var name = (_c = (_b = row.name) !== null && _b !== void 0 ? _b : row.date) !== null && _c !== void 0 ? _c : '';
    var rows = items.map(function (_a) {
        var key = _a.key, value = _a.value;
        return ({
            key: key,
            label: "".concat(key, ": "),
            value: formatValue(value, key)
        });
    });
    return (_jsxs(TooltipPanelCompact, { children: [_jsx(TooltipDateHeaderSection, { children: renderTooltipDateHeading(name, { referenceDateFrom: referenceDateFrom, referenceDateTo: referenceDateTo }) }), _jsx(CategoryDotRows, { rows: rows, getDotColorClass: getDotColorClass })] }));
};
export function TremorBarChartTooltip(_a) {
    var categories = _a.categories, getDotColorClass = _a.getDotColorClass, formatValue = _a.formatValue, hideWhenAllZero = _a.hideWhenAllZero, tremorProps = __rest(_a, ["categories", "getDotColorClass", "formatValue", "hideWhenAllZero"]);
    var _b = useFilters(), dateFrom = _b.dateFrom, dateTo = _b.dateTo;
    return getBarChartTooltip(tremorProps, categories, {
        getDotColorClass: getDotColorClass,
        formatValue: formatValue,
        referenceDateFrom: dateFrom,
        referenceDateTo: dateTo,
        hideWhenAllZero: hideWhenAllZero
    });
}
export var createGetDotColorClass = function (_a) {
    var categories = _a.categories, colors = _a.colors, fallbackClass = _a.fallbackClass, _b = _a.prefix, prefix = _b === void 0 ? 'bg-' : _b;
    var defaultClass = fallbackClass !== null && fallbackClass !== void 0 ? fallbackClass : (colors.length > 0 ? "".concat(prefix).concat(colors[0]) : '');
    return function (key) {
        var index = categories.indexOf(key);
        var color = index >= 0 && colors[index] ? colors[index] : null;
        if (!color)
            return defaultClass;
        return "".concat(prefix).concat(color);
    };
};
